import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import { workspacesApi } from "../../../actions";
import { brand, notice } from "../../../theme/businessFlowsTheme";
import { canPlatform } from "../../../utils/permissions";
import WorkspaceView from "../../workspaces/WorkspaceView";
import { EmptyState, ListSkeleton, Refusal, Surface, Toast, dialogPaper, focusRing, rowSx } from "../../workspaces/WorkspaceUi";
import { filterWorkspaces, initials, nameInitials, planLabel, seatText } from "../../workspaces/workspaceFormat";

function WorkspaceCard({ workspace, onOpen }) {
  const seats = workspace.seats;
  const share = seats?.allowed ? Math.min(100, (seats.used / seats.allowed) * 100) : 0;
  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      role="button"
      tabIndex={0}
      aria-label={`Open ${workspace.name}`}
      sx={{
        p: 2.25,
        borderRadius: 4,
        border: `1px solid ${brand.line}`,
        cursor: "pointer",
        transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": { borderColor: brand.ink, boxShadow: "0 12px 32px -24px rgba(26,26,46,0.6)" },
        "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal, fontWeight: 800, fontSize: "0.85rem" }}>
          {nameInitials(workspace.name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{workspace.name}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={planLabel(workspace.plan)} sx={{ height: 20, bgcolor: brand.signal, color: brand.ink, fontWeight: 700 }} />
            {workspace.noAdmin && (
              <Chip size="small" variant="outlined" label="No admin" sx={{ height: 20, color: brand.muted, borderColor: brand.line }} />
            )}
            {workspace.inSeveral > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${workspace.inSeveral} in several workspaces`}
                sx={{ height: 20, color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
              />
            )}
            {workspace.legacy && (
              <Chip
                size="small"
                variant="outlined"
                label="Needs setting up"
                sx={{ height: 20, color: notice.quiet.fg, bgcolor: notice.quiet.bg, borderColor: notice.quiet.line }}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {seatText(seats)}
      </Typography>
      {seats?.allowed ? (
        <LinearProgress
          variant="determinate"
          value={share}
          sx={{ mt: 0.5, height: 6, borderRadius: 999, bgcolor: brand.canvas, "& .MuiLinearProgress-bar": { bgcolor: seats.used >= seats.allowed ? notice.fix.fg : brand.ink } }}
        />
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.75 }}>
        <StorageRoundedIcon fontSize="small" sx={{ color: brand.signalDeep }} />
        <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
          {workspace.databases.length ? workspace.databases.map((d) => d.label).join(", ") : "No databases yet"}
        </Typography>
        {workspace.admins.length > 0 && (
          <AvatarGroup max={3} sx={{ "& .MuiAvatar-root": { width: 26, height: 26, fontSize: "0.65rem", bgcolor: brand.inkSoft } }}>
            {workspace.admins.map((admin) => (
              <Avatar key={admin.id} alt={admin.name}>
                {initials(admin)}
              </Avatar>
            ))}
          </AvatarGroup>
        )}
      </Stack>
    </Paper>
  );
}

// Console → Workspaces: every workspace, filters, New workspace; one opens as
// a WorkspaceView at /admin/workspaces/:id.
export default function WorkspacesPage({ user }) {
  const history = useHistory();
  const location = useLocation();
  const openId = location.pathname.split("/")[3] || null;
  const mayCreate = canPlatform(user, "tenants.manage");

  const [workspaces, setWorkspaces] = useState(null);
  const [archived, setArchived] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    const [active, old] = await Promise.all([workspacesApi.list(), workspacesApi.list(true)]);
    if (active.success) {
      setWorkspaces(active.content);
      setError("");
    } else setError(active.msg || "Couldn't load the workspaces.");
    setArchived(old.success ? old.content : []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => filterWorkspaces(workspaces || [], { query, filter }), [workspaces, query, filter]);
  const open = (id) => history.push(`/admin/workspaces/${encodeURIComponent(id)}`);
  const back = (msg) => {
    history.push("/admin/workspaces");
    if (typeof msg === "string") setToast(msg);
    load();
  };

  const create = async () => {
    setBusy(true);
    const res = await workspacesApi.create(name.trim());
    setBusy(false);
    if (!res.success) {
      setCreating(false);
      setError(res.msg || "Couldn't create the workspace.");
      return;
    }
    setCreating(false);
    setName("");
    setToast(res.msg || `${name.trim()} is ready. Add its databases, then invite its admin.`);
    await load();
    open(res.content.id);
  };

  if (openId) {
    return <WorkspaceView workspaceId={decodeURIComponent(openId)} user={user} audience="console" onBack={back} onChanged={load} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }} sx={{ mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Workspaces
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A workspace has people, groups and databases. One without an admin is run by bpExperts.
          </Typography>
        </Box>
        {mayCreate && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreating(true)}>
            New workspace
          </Button>
        )}
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="Search by name, admin or database"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          sx={{ maxWidth: { md: 360 } }}
        />
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, next) => next && setFilter(next)} aria-label="Filter workspaces">
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="noAdmin">No admin</ToggleButton>
          <ToggleButton value="several">Belongs to several workspaces</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      {!workspaces ? (
        <ListSkeleton rows={3} height={150} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<WorkspacesRoundedIcon />}
          title={workspaces.length ? "No workspace matches" : "No workspaces yet"}
          action={
            workspaces.length ? (
              <Button
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
              >
                Show all workspaces
              </Button>
            ) : mayCreate ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreating(true)}>
                New workspace
              </Button>
            ) : null
          }
        >
          {workspaces.length ? "Clear the search or choose All." : "Create the first workspace, then invite its admin."}
        </EmptyState>
      ) : (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))" }}>
          {shown.map((workspace) =>
            workspace.legacy ? (
              // Created by the early seed scripts without an id: opening it sets it up.
              <WorkspaceCard
                key={workspace.name}
                workspace={workspace}
                onOpen={async () => {
                  const res = await workspacesApi.create(workspace.name);
                  if (res.success) {
                    setToast(res.msg);
                    await load();
                    open(res.content.id);
                  } else setError(res.msg || "Couldn't set up this workspace.");
                }}
              />
            ) : (
              <WorkspaceCard key={workspace.id} workspace={workspace} onOpen={() => open(workspace.id)} />
            )
          )}
        </Box>
      )}

      {archived.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Archived
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nothing is deleted. Restoring a workspace brings back its people, groups and databases as they were.
          </Typography>
          <Surface>
            {archived.map((workspace, index) => (
              <Stack key={workspace.id || workspace.name} direction="row" spacing={1.5} alignItems="center" sx={rowSx(index === 0)}>
                <Typography sx={{ flex: 1, color: "text.secondary" }}>{workspace.name}</Typography>
                {mayCreate && workspace.id && (
                  <Button
                    size="small"
                    startIcon={<RestoreRoundedIcon />}
                    onClick={async () => {
                      const res = await workspacesApi.restore(workspace.id);
                      if (res.success) {
                        setToast(res.msg);
                        load();
                      } else setError(res.msg || "Couldn't restore the workspace.");
                    }}
                  >
                    Restore
                  </Button>
                )}
              </Stack>
            ))}
          </Surface>
        </Box>
      )}

      <Dialog open={creating} onClose={busy ? undefined : () => setCreating(false)} fullWidth maxWidth="xs" PaperProps={dialogPaper}>
        <DialogTitle sx={{ fontWeight: 700 }}>New workspace</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Next you add its databases and invite its admin.
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && !busy) create();
            }}
          >
            <TextField autoFocus fullWidth label="Workspace name" placeholder="e.g. Acme GmbH" value={name} onChange={(e) => setName(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreating(false)} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={busy} disabled={!name.trim()} onClick={create}>
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
